import React from 'react';
import { gql } from '@apollo/client';
import { Stack } from '@moda/om';
import { Availability, FulfillmentTypes } from '@moda/portal-stanchions';
import { PdpInfoContainerFragment, SizeInventoryFragment } from '../../generated/types';
import { SIZE_INVENTORY_FRAGMENT } from '../SizeSelector';
import { PdpTrunkshowInfo } from './PdpTrunkshowInfo';
import { PdpDeliveryInfo } from './PdpDeliveryInfo';
import { formatDeliveryDate } from './formatDeliveryDate';

interface Props {
  variant: PdpInfoContainerFragment;
  selectedSize: SizeInventoryFragment | null;
}

export const PDP_INFO_MODULE_FRAGMENT = gql`
  fragment PdpInfoContainerFragment on Variant {
    id
    availability
    fulfilmentType: fulfilment_type
    customerDeliveryNotes: customer_delivery_notes
    masterVariant: master_variants_data {
      processDeliveryDates: process_delivery_dates {
        deliveryStartsOn: delivery_starts_on
        deliveryEndsOn: delivery_ends_on
      }
      inventory {
        ...SizeInventoryFragment
      }
    }
    otherVariants: other_variants_data {
      processDeliveryDates: process_delivery_dates {
        deliveryStartsOn: delivery_starts_on
        deliveryEndsOn: delivery_ends_on
      }
    }
    trunkshows {
      edges {
        node {
          id
          name
          slug
          seasonName: season_name
          activeStartI: active_start_i
          activeEndI: active_end_i
        }
      }
    }
  }
  ${SIZE_INVENTORY_FRAGMENT}
`;

export const PdpInfoContainer: React.FC<Props> = ({ variant, selectedSize }) => {
  const trunkshow = variant?.trunkshows?.edges?.[0]?.node;
  const trunkshowDate = variant.masterVariant.processDeliveryDates?.deliveryEndsOn;
  const trunkshowDateNew = trunkshowDate != null ? new Date(trunkshowDate) : null;

  const shippingDateFormatted = trunkshowDateNew ? formatDeliveryDate(trunkshowDateNew) : undefined;

  const isDropshipItem =
    variant.fulfilmentType === FulfillmentTypes.DropShip ||
    variant.fulfilmentType === FulfillmentTypes.MiraklDropship;

  const showTrunkshowInfo = variant.availability === String(Availability.Trunkshow) && trunkshow;

  const showNotes =
    variant.fulfilmentType === FulfillmentTypes.Fulfillment && variant.customerDeliveryNotes !== '';

  if (!isDropshipItem && !showTrunkshowInfo && !showNotes) return null;

  return (
    <Stack space={5} data-testid="infoModuleSection">
      {showTrunkshowInfo && (
        <PdpTrunkshowInfo
          trunkshow={trunkshow}
          shippingDateFormatted={shippingDateFormatted}
          pdpVersion
          isDropshipItem={isDropshipItem}
        />
      )}

      {isDropshipItem && (
        <PdpDeliveryInfo
          deliveryDates={
            selectedSize?.processDeliveryDates || variant.masterVariant.processDeliveryDates
          }
          fulfilmentType={variant.fulfilmentType}
          customerDeliveryNotes={variant.customerDeliveryNotes}
        />
      )}

      {showNotes && variant.customerDeliveryNotes}
    </Stack>
  );
};
